/* ==========================================================================
   SOFT STYLE JS
   1. <sf-slider>    native scroll slider with dots, arrows, autoplay
   2. <sf-tabs>      product info tabs
   3. <sf-delivery>  ships / arrives date estimate
   4. <sf-upsell>    "Frequently purchased with" add to cart
   5. [data-sf-atc]  buttons that trigger the main product add to cart
   6. [data-sf-scroll-reviews] smooth scroll to the reviews widget
   ========================================================================== */

(function () {
  'use strict';

  /* 1. Slider ------------------------------------------------------------ */
  if (!customElements.get('sf-slider')) {
    class SfSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-sf-track]');
        if (!this.track) return;
        this.dotsWrap = this.querySelector('[data-sf-dots]');
        this.prev = this.querySelector('[data-sf-prev]');
        this.next = this.querySelector('[data-sf-next]');
        this.positions = [];

        this.onScroll = this.onScroll.bind(this);
        this.build = this.build.bind(this);
        this.track.addEventListener('scroll', this.onScroll, { passive: true });

        if (this.prev) this.prev.addEventListener('click', () => this.go(this.current() - 1));
        if (this.next) this.next.addEventListener('click', () => this.go(this.current() + 1));

        if ('ResizeObserver' in window) {
          this.ro = new ResizeObserver(() => {
            clearTimeout(this.rt);
            this.rt = setTimeout(this.build, 120);
          });
          this.ro.observe(this.track);
        }
        this.build();
        this.initAutoplay();
      }

      disconnectedCallback() {
        if (this.ro) this.ro.disconnect();
        clearInterval(this.timer);
      }

      slides() {
        return Array.from(this.track.children).filter((el) => el.offsetParent !== null || el.offsetWidth > 0);
      }

      build() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        const start = this.track.getBoundingClientRect().left - this.track.scrollLeft;
        const pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
        const seen = new Set();
        this.positions = [];
        this.slides().forEach((slide) => {
          let pos = Math.round(slide.getBoundingClientRect().left - start - pad);
          pos = Math.max(0, Math.min(pos, Math.round(max)));
          if (!seen.has(pos)) {
            seen.add(pos);
            this.positions.push(pos);
          }
        });
        if (max <= 2) this.positions = [0];

        if (this.dotsWrap) {
          this.dotsWrap.innerHTML = '';
          if (this.positions.length > 1) {
            this.positions.forEach((pos, i) => {
              const dot = document.createElement('button');
              dot.type = 'button';
              dot.className = 'sf-slider__dot';
              dot.setAttribute('aria-label', String(i + 1));
              dot.addEventListener('click', () => this.go(i));
              this.dotsWrap.appendChild(dot);
            });
          }
        }
        this.onScroll();
      }

      current() {
        const x = this.track.scrollLeft;
        let best = 0;
        let bestDist = Infinity;
        this.positions.forEach((pos, i) => {
          const d = Math.abs(pos - x);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });
        return best;
      }

      go(index) {
        if (!this.positions.length) return;
        const i = Math.max(0, Math.min(index, this.positions.length - 1));
        this.track.scrollTo({ left: this.positions[i], behavior: 'smooth' });
      }

      onScroll() {
        const i = this.current();
        if (this.dotsWrap) {
          Array.from(this.dotsWrap.children).forEach((dot, n) => {
            dot.setAttribute('aria-current', n === i ? 'true' : 'false');
          });
        }
        if (this.prev) this.prev.disabled = i <= 0;
        if (this.next) this.next.disabled = i >= this.positions.length - 1;
      }

      initAutoplay() {
        const delay = parseInt(this.dataset.autoplay, 10);
        if (!delay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        let paused = false;
        const pause = () => (paused = true);
        const resume = () => (paused = false);
        this.addEventListener('pointerenter', pause);
        this.addEventListener('pointerleave', resume);
        this.addEventListener('focusin', pause);
        this.addEventListener('focusout', resume);
        this.track.addEventListener('touchstart', pause, { passive: true });
        this.timer = setInterval(() => {
          if (paused || document.hidden || this.positions.length < 2) return;
          const i = this.current();
          this.go(i >= this.positions.length - 1 ? 0 : i + 1);
        }, delay * 1000);
      }
    }
    customElements.define('sf-slider', SfSlider);
  }

  /* 2. Tabs -------------------------------------------------------------- */
  if (!customElements.get('sf-tabs')) {
    class SfTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        this.tabs.forEach((tab, i) => {
          tab.addEventListener('click', () => this.select(i));
          tab.addEventListener('keydown', (e) => {
            if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
            e.preventDefault();
            const next = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(next);
            this.tabs[next].focus();
          });
        });
      }

      select(index) {
        this.tabs.forEach((tab, i) => {
          const on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.tabIndex = on ? 0 : -1;
          if (this.panels[i]) this.panels[i].hidden = !on;
        });
      }
    }
    customElements.define('sf-tabs', SfTabs);
  }

  /* 3. Delivery estimate -------------------------------------------------- */
  if (!customElements.get('sf-delivery')) {
    class SfDelivery extends HTMLElement {
      connectedCallback() {
        const d = this.dataset;
        const tz = d.tz || undefined;
        const cutoff = parseInt(d.cutoff, 10);
        const cutoffSat = parseInt(d.cutoffSat, 10);
        const process = parseInt(d.process, 10) || 0;
        const min = parseInt(d.min, 10) || 0;
        const max = Math.max(parseInt(d.max, 10) || 0, min);
        const skip = (d.skip || '')
          .split(',')
          .map((s) => parseInt(s, 10))
          .filter((n) => !isNaN(n));
        const locale = document.documentElement.lang || 'en';

        let now;
        try {
          const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            hour12: false,
          }).formatToParts(new Date());
          const get = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
          now = { y: get('year'), m: get('month'), d: get('day'), h: get('hour') % 24 };
        } catch (e) {
          const n = new Date();
          now = { y: n.getFullYear(), m: n.getMonth() + 1, d: n.getDate(), h: n.getHours() };
        }

        const isWorkday = (date) => skip.indexOf(date.getDay()) === -1;
        const addWorkdays = (date, days) => {
          const r = new Date(date);
          let left = days;
          while (left > 0) {
            r.setDate(r.getDate() + 1);
            if (isWorkday(r)) left -= 1;
          }
          return r;
        };

        const today = new Date(now.y, now.m - 1, now.d, 12);
        let ship = new Date(today);
        const dayCutoff = today.getDay() === 6 && !isNaN(cutoffSat) ? cutoffSat : cutoff;
        const pastCutoff = !isNaN(dayCutoff) && now.h >= dayCutoff;
        if (!isWorkday(ship) || pastCutoff) {
          do {
            ship.setDate(ship.getDate() + 1);
          } while (!isWorkday(ship));
        }
        ship = addWorkdays(ship, process);

        const fmt = (date) => date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
        const weekday = (date) => date.toLocaleDateString(locale, { weekday: 'short' });

        const from = addWorkdays(ship, min);
        const to = addWorkdays(ship, max);

        const dayDiff = Math.round((ship - today) / 86400000);
        let shipText = weekday(ship) + ', ' + fmt(ship);
        if (dayDiff === 0 && d.todayLabel) shipText = d.todayLabel;
        if (dayDiff === 1 && d.tomorrowLabel) shipText = d.tomorrowLabel;
        this.querySelectorAll('[data-sf-ship]').forEach((el) => {
          el.textContent = shipText;
        });
        this.querySelectorAll('[data-sf-arrive]').forEach((el) => {
          el.textContent = min === max ? fmt(from) : fmt(from) + ' – ' + fmt(to);
        });
        this.classList.add('is-ready');
      }
    }
    customElements.define('sf-delivery', SfDelivery);
  }

  /* Shared add-to-cart helper (renders Dawn's cart drawer) ---------------- */
  function addToCart(items) {
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const body = { items: items };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((s) => s.id);
      body.sections_url = window.location.pathname;
    }
    return fetch(window.routes.cart_add_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((state) => {
        if (state.status) throw new Error(state.description || state.message || 'Error');
        if (cart && typeof cart.renderContents === 'function') {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          // renderContents expects the first item's id/key like Dawn's product form
          const first = state.items ? state.items[0] : state;
          cart.renderContents(Object.assign({}, first, { sections: state.sections }));
        } else {
          window.location.href = window.routes.cart_url;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'sf-upsell', cartData: state });
        }
        return state;
      });
  }

  /* 4. Upsell ------------------------------------------------------------- */
  if (!customElements.get('sf-upsell')) {
    class SfUpsell extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('form[data-sf-upsell-form]').forEach((form) => {
          form.addEventListener('submit', (e) => {
            e.preventDefault();
            const btn = form.querySelector('[type="submit"]');
            const select = form.querySelector('select[name="id"]');
            const input = form.querySelector('input[name="id"]');
            const id = Number((select || input).value);
            if (!id || !btn) return;
            const label = btn.innerHTML;
            btn.setAttribute('aria-disabled', 'true');
            btn.classList.add('is-loading');
            addToCart([{ id: id, quantity: 1 }])
              .then(() => {
                btn.innerHTML = btn.dataset.added || label;
                setTimeout(() => (btn.innerHTML = label), 2200);
              })
              .catch((err) => {
                const msg = form.querySelector('[data-sf-upsell-error]');
                if (msg) {
                  msg.textContent = err.message;
                  msg.hidden = false;
                }
              })
              .finally(() => {
                btn.removeAttribute('aria-disabled');
                btn.classList.remove('is-loading');
              });
          });
        });
      }
    }
    customElements.define('sf-upsell', SfUpsell);
  }

  /* 5. Buttons that trigger the main product form ------------------------- */
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-sf-atc]');
    if (!trigger) return;
    const submit = document.querySelector('product-info .product-form__submit, .product-form__submit');
    if (!submit) return; // falls back to the link href
    e.preventDefault();
    if (submit.disabled || submit.getAttribute('aria-disabled') === 'true') {
      submit.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    submit.click();
  });

  /* 6. Scroll to reviews -------------------------------------------------- */
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-sf-scroll-reviews]');
    if (!link) return;
    const custom = link.dataset.target;
    const target =
      (custom && document.querySelector(custom)) ||
      document.querySelector('#looxReviews, [id^="looxReviews"], .loox-reviews-default, #shopify-product-reviews, [data-sf-reviews]');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
})();
